import _isEmpty from "lodash/isEmpty";
import styles from "./PaymentOptions.module.scss";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import PaymentOption from "./Option/PaymentOption";
import { useState } from "react";
import { dispatchAnalyticsEvent } from "../../../../../utils";
import { useTradeInContext } from "../../../TradeInContext";
import { createQuote, CreateQuoteResponse } from "../../../client/createQuote";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { TRADE_IN_STEPS } from "../TradeInModal";
import NextStep, { TradeInSteps } from "../NextStep/NextStep";
import { TRADE_IN_UNAVAILABLE_WARNING } from "../DeviceSearch/types";
import { TradeInEvents } from "../../../TradeIn";
import { EnquiryResponse } from "../Grading/types";

export enum PaymentOptionType {
  CASH = "CASH",
  GIFT_CARD = "GIFT_CARD",
}
export interface AvailablePaymentOption {
  type: PaymentOptionType;
  displayValue: string;
  value: string;
}

export interface QuotesResponse {
  id: string;
  deviceName: string;
  expiry: string;
  payment: AvailablePaymentOption;
}

function getRequestBody(
  enquiryOptions: EnquiryResponse,
  selectedPaymentOption: AvailablePaymentOption,
  deviceIdentifier: string,
) {
  const requestBody: {
    enquiryId: string;
    paymentType: PaymentOptionType;
    deviceIdentifier?: string;
  } = {
    enquiryId: enquiryOptions.id,
    paymentType: selectedPaymentOption.type,
  };

  if (deviceIdentifier) {
    requestBody.deviceIdentifier = deviceIdentifier;
  }

  return requestBody;
}

const PaymentOptions = () => {
  const {
    tradeInBaseUrl,
    selectedDevice,
    enquiryOptions,
    paymentOptions,
    setQuoteDetail,
    setCurrentScreen,
    onApiError,
    deviceIdentifier,
  } = useTradeInContext();
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<AvailablePaymentOption>();
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);

  if (_isEmpty(paymentOptions)) return null;

  const handleCreateQuote = async (
    selectedPaymentOption: AvailablePaymentOption,
  ): Promise<void> => {
    if (!enquiryOptions) {
      return;
    }

    setIsLoading(true);

    const timeoutController = new AbortController();

    const timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, 13000);

    const response: CreateQuoteResponse = await createQuote(
      tradeInBaseUrl,
      getRequestBody(enquiryOptions, selectedPaymentOption, deviceIdentifier),
      timeoutController,
    );

    clearTimeout(timeoutId);

    if (response.status < 200 || response.status > 299) {
      setIsLoading(false);
      onApiError(TRADE_IN_UNAVAILABLE_WARNING);
      return;
    }
    setIsLoading(false);
    onQuoteCreated(response.quote as QuotesResponse);
  };

  const handleContinue = async () => {
    if (selectedPaymentOption) {
      dispatchTradeInPaymentSelected(selectedPaymentOption?.type);
      await handleCreateQuote(selectedPaymentOption);
    }
  };
  const onSelect = (index: number) => {
    setSelectedPaymentOption(paymentOptions[index]);
    setSelectedIndex(index);
  };

  const dispatchTradeInPaymentSelected = (paymentOptionType: string) => {
    dispatchAnalyticsEvent(
      "global.interaction",
      {
        action: "global.interaction",
        type: "pdp",
        subType: "trade in - your payout options - continue",
        additionalInfo: { paymentOptionType },
        properties: {},
        products: [],
      },
      {},
    );
  };

  const onQuoteCreated = (quote: QuotesResponse) => {
    setQuoteDetail(quote);
    setCurrentScreen(TRADE_IN_STEPS.CONFIRMATION);
    dispatchMonetateQEvent(
      TradeInEvents.PW_TRADE_IN_YOUR_PAYOUT_OPTIONS_STEP_CTA,
    );
  };

  if (!selectedDevice) {
    return null;
  }

  return (
    <div className={styles.paymentOptions}>
      <div className={styles.paymentOptions__wrapper}>
        <h2 className={styles.paymentOptions__heading}>Your payout options</h2>
        <p>Please choose how you would like to be paid</p>

        <ul className={styles.paymentOptions__options}>
          {paymentOptions.map((payment, index) => (
            <li key={index} className={styles.paymentOptions__optionsContainer}>
              <PaymentOption
                description={selectedDevice.description}
                key={index}
                index={index}
                payment={payment}
                selected={index === selectedIndex}
                onSelect={() => onSelect(index)}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.paymentOptions__footer}>
        <div className={styles.paymentOptions__continueBtnWrapper}>
          <PrimaryButton
            className={styles.paymentOptions__continueBtn}
            tag="button"
            type="button"
            data-cy="continue-btn"
            disabled={!selectedPaymentOption}
            onClick={handleContinue}
            submitting={isLoading}
          >
            Continue
          </PrimaryButton>
        </div>
        <NextStep step={TradeInSteps.REVIEW} />
      </div>
    </div>
  );
};
export default PaymentOptions;
